import React, {
  useEffect,
  useState,
} from "react";

import {
  FiCopy,
  FiExternalLink,
  FiSave,
  FiGlobe,
  FiCode,
  FiRefreshCw,
} from "react-icons/fi";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://eduleads.yatranest.in";

const PublicLeads = () => {
  const [settings, setSettings] =
    useState({
      enabled: true,
      requireEmail: false,
      requirePhone: true,
      allowWhatsApp: true,
      showCourse: true,
      showBudget: false,
      showMessage: true,
    });

  const [
    publicLeadKey,
    setPublicLeadKey,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    saved,
    setSaved,
  ] = useState(false);

  const getToken = () => {
    return (
      localStorage.getItem(
        "token"
      ) ||
      localStorage.getItem(
        "accessToken"
      )
    );
  };

  const authHeaders = () => {
    const token =
      getToken();

    return {
      "Content-Type":
        "application/json",

      ...(token
        ? {
            Authorization:
              `Bearer ${token}`,
          }
        : {}),
    };
  };

  const loadSettings =
    async () => {
      try {
        setLoading(true);

        const response =
          await fetch(
            `${API_BASE}/api/organization/public-leads/settings`,
            {
              headers:
                authHeaders(),
            }
          );

        const result =
          await response.json();

        if (!response.ok) {
          throw new Error(
            result.message ||
              "Unable to load settings."
          );
        }

        const data =
          result.data;

        setPublicLeadKey(
          data.publicLeadKey
        );

        const form =
          data.publicLeadFormSettings ||
          {};

        setSettings({
          enabled:
            data.websiteLeadCaptureEnabled !==
            false,

          requireEmail:
            form.requireEmail ??
            false,

          requirePhone:
            form.requirePhone ??
            true,

          allowWhatsApp:
            form.allowWhatsApp ??
            true,

          showCourse:
            form.showCourse ??
            true,

          showBudget:
            form.showBudget ??
            false,

          showMessage:
            form.showMessage ??
            true,
        });
      } catch (error) {
        console.error(
          "Unable to load public lead settings:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange =
    async (event) => {
      const {
        name,
        checked,
      } = event.target;

      setSettings(
        (prev) => ({
          ...prev,
          [name]: checked,
        })
      );

      setSaved(false);

      if (name === "enabled") {
        try {
          await fetch(
            `${API_BASE}/api/organization/public-leads/capture`,
            {
              method: "PUT",

              headers:
                authHeaders(),

              body:
                JSON.stringify({
                  enabled:
                    checked,
                }),
            }
          );
        } catch (error) {
          console.error(
            "Unable to update capture status:",
            error
          );
        }
      }
    };

  const saveSettings =
    async () => {
      try {
        setSaving(true);
        setSaved(false);

        const response =
          await fetch(
            `${API_BASE}/api/organization/public-leads/settings`,
            {
              method: "PUT",

              headers:
                authHeaders(),

              body:
                JSON.stringify({
                  requireEmail:
                    settings.requireEmail,

                  requirePhone:
                    settings.requirePhone,

                  allowWhatsApp:
                    settings.allowWhatsApp,

                  showCourse:
                    settings.showCourse,

                  showBudget:
                    settings.showBudget,

                  showMessage:
                    settings.showMessage,
                }),
            }
          );

        const result =
          await response.json();

        if (!response.ok) {
          throw new Error(
            result.message ||
              "Unable to save settings."
          );
        }

        setSaved(true);
      } catch (error) {
        console.error(
          "Unable to save settings:",
          error
        );
      } finally {
        setSaving(false);
      }
    };

  const regenerateKey =
    async () => {
      if (
        !window.confirm(
          "Regenerating the public key will stop the existing widget from working until its installation code is updated. Continue?"
        )
      ) {
        return;
      }

      try {
        const response =
          await fetch(
            `${API_BASE}/api/organization/public-leads/regenerate-key`,
            {
              method: "POST",
              headers:
                authHeaders(),
            }
          );

        const result =
          await response.json();

        if (!response.ok) {
          throw new Error(
            result.message ||
              "Unable to regenerate key."
          );
        }

        setPublicLeadKey(
          result.data.publicLeadKey
        );

        setSaved(true);
      } catch (error) {
        console.error(
          "Unable to regenerate public key:",
          error
        );
      }
    };

  const publicUrl =
    `${window.location.origin}/public-lead`;

  const widgetCode =
    publicLeadKey
      ? `<div id="eduleads-widget"></div>
<script
  src="${API_BASE}/widget/eduleads-widget.js"
  data-public-key="${publicLeadKey}"
  data-container="eduleads-widget"
></script>`
      : "";

  const copyText =
    async (text) => {
      try {
        await navigator.clipboard.writeText(
          text
        );

        setSaved(true);
      } catch (error) {
        console.error(
          "Unable to copy:",
          error
        );
      }
    };

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="text-center py-5">
            Loading public lead settings...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">

        <div className="mb-4">
          <h4 className="mb-1">
            Website Lead Capture
          </h4>

          <p className="text-muted mb-0">
            Configure and control enquiries
            captured from client websites.
          </p>
        </div>

        {/* MASTER SWITCH */}

        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex align-items-center">

              <div className="avatar-sm bg-light rounded d-flex align-items-center justify-content-center me-3">
                <FiGlobe size={22} />
              </div>

              <div className="flex-grow-1">
                <h5 className="mb-1">
                  Public Enquiry Form
                </h5>

                <p className="text-muted mb-0">
                  Accept student enquiries
                  from your website.
                </p>
              </div>

              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="enabled"
                  checked={
                    settings.enabled
                  }
                  onChange={
                    handleChange
                  }
                  id="publicLeadEnabled"
                />

                <label
                  className="form-check-label"
                  htmlFor="publicLeadEnabled"
                >
                  {settings.enabled
                    ? "Enabled"
                    : "Disabled"}
                </label>
              </div>

            </div>
          </div>
        </div>

        {/* FORM SETTINGS */}

        <div className="card mb-4">
          <div className="card-header">
            <h5 className="card-title mb-0">
              Form Fields
            </h5>
          </div>

          <div className="card-body">
            <div className="row">

              <div className="col-lg-6">

                {[
                  [
                    "requirePhone",
                    "Require phone number",
                  ],
                  [
                    "requireEmail",
                    "Require email",
                  ],
                  [
                    "allowWhatsApp",
                    "Allow WhatsApp number",
                  ],
                ].map(
                  ([name, label]) => (
                    <div
                      className="form-check form-switch mb-3"
                      key={name}
                    >
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name={name}
                        checked={
                          settings[name]
                        }
                        onChange={
                          handleChange
                        }
                        id={name}
                      />

                      <label
                        className="form-check-label"
                        htmlFor={name}
                      >
                        {label}
                      </label>
                    </div>
                  )
                )}

              </div>

              <div className="col-lg-6">

                {[
                  [
                    "showCourse",
                    "Show course / program field",
                  ],
                  [
                    "showBudget",
                    "Show budget field",
                  ],
                  [
                    "showMessage",
                    "Show enquiry message",
                  ],
                ].map(
                  ([name, label]) => (
                    <div
                      className="form-check form-switch mb-3"
                      key={name}
                    >
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name={name}
                        checked={
                          settings[name]
                        }
                        onChange={
                          handleChange
                        }
                        id={name}
                      />

                      <label
                        className="form-check-label"
                        htmlFor={name}
                      >
                        {label}
                      </label>
                    </div>
                  )
                )}

              </div>

            </div>

            <div className="d-flex justify-content-end mt-3">
              <button
                className="btn btn-primary"
                onClick={
                  saveSettings
                }
                disabled={saving}
              >
                <FiSave
                  size={15}
                  className="me-1"
                />

                {saving
                  ? "Saving..."
                  : "Save Settings"}
              </button>
            </div>

            {saved && (
              <div className="alert alert-success mt-3 mb-0">
                Settings saved successfully.
              </div>
            )}
          </div>
        </div>

        {/* WIDGET */}

        <div className="card mb-4">
          <div className="card-header">
            <h5 className="card-title mb-0">
              Website Widget
            </h5>
          </div>

          <div className="card-body">

            <p className="text-muted">
              Install this small script on
              the client's website once.
              After installation, the widget
              can be controlled from this page.
            </p>

            <div className="alert alert-info">
              <strong>
                How it works:
              </strong>{" "}
              Install once → control from
              EduLeads.
            </div>

            <label className="form-label">
              Installation Code
            </label>

            <textarea
              className="form-control"
              rows="5"
              value={widgetCode}
              readOnly
            />

            <div className="mt-3">
              <button
                className="btn btn-primary me-2"
                disabled={!widgetCode}
                onClick={() =>
                  copyText(widgetCode)
                }
              >
                <FiCode
                  size={15}
                  className="me-1"
                />
                Copy Widget Code
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={
                  regenerateKey
                }
              >
                <FiRefreshCw
                  size={15}
                  className="me-1"
                />
                Regenerate Key
              </button>
            </div>

            <small className="text-muted d-block mt-3">
              Regenerating the key invalidates
              the previous widget installation.
            </small>

          </div>
        </div>

        {/* PUBLIC URL */}

        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">
              Public Form URL
            </h5>
          </div>

          <div className="card-body">

            <div className="input-group">

              <input
                type="text"
                className="form-control"
                value={
                  publicUrl
                }
                readOnly
              />

              <button
                className="btn btn-light"
                onClick={() =>
                  copyText(
                    publicUrl
                  )
                }
              >
                <FiCopy
                  size={15}
                  className="me-1"
                />
                Copy
              </button>

              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-light"
              >
                <FiExternalLink
                  size={15}
                  className="me-1"
                />
                Open
              </a>

            </div>

            <small className="text-muted d-block mt-2">
              Use this URL when the client
              doesn't want an embedded widget.
            </small>

          </div>
        </div>

      </div>
    </div>
  );
};

export default PublicLeads;